import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Plus, Download, Upload, Search, Filter, ChevronLeft, ChevronRight,
  FileText, Send, Eye, Trash2, Edit2, CheckCircle, XCircle, Clock,
  AlertCircle, RefreshCw, Building2, User, Calendar, DollarSign, X,
  ExternalLink, QrCode, Copy, Printer, MoreVertical, Ban
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import PortalLayout from './PortalLayout';
import { getGlobalPropertyId, setGlobalProperty } from './propertySync';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function PortalOwnerPayments() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  
  // Filters - initialize from global property selector
  const [filters, setFilters] = useState(() => {
    const savedPropId = localStorage.getItem('einvoice_selected_property') || '';
    return {
      property_id: savedPropId,
      status: '',
      source: '',
      search: '',
      date_from: '',
      date_to: ''
    };
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Selection for batch actions
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showInvoiceView, setShowInvoiceView] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrPayment, setQrPayment] = useState(null);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    owner_name: '',
    owner_ic_brn: '',
    owner_tin: 'EI00000000010',
    amount: '',
    description: '',
    property_id: '',
    classification_code: '036',
    owner_address: '',
    owner_city: '',
    owner_state: '',
    owner_postal_code: '',
    owner_phone: '',
    owner_email: '',
    is_foreigner: false
  });
  
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const token = localStorage.getItem('einvoice_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      
      if (filters.property_id) params.append('property_id', filters.property_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.source) params.append('source', filters.source);
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const { data } = await axios.get(`${API_URL}/api/einvoice/owner-payments?${params}`, { headers });
      setPayments(data.payments || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      console.error('Failed to load payments:', err);
      if (err.response?.status === 401) {
        navigate('/einvoice/portal/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/einvoice/portal/properties`, { headers });
      setProperties(data.properties || []);
      if (data.properties?.length > 0 && !form.property_id) {
        setForm(f => ({ ...f, property_id: data.properties[0].id }));
      }
    } catch (err) {
      console.error('Failed to load properties:', err);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/einvoice/portal/auth');
      return;
    }
    loadPayments();
    loadProperties();

    // Sync with global property selector
    const handlePropertyChange = (e) => {
      const prop = e.detail;
      setFilters(f => ({ ...f, property_id: prop ? prop.id : '' }));
      setPage(1);
    };
    window.addEventListener('property-changed', handlePropertyChange);
    return () => window.removeEventListener('property-changed', handlePropertyChange);
  }, [page, filters]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/einvoice/owner-payments/template`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'owner_payment_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!form.property_id) {
      toast.error('Please select a property first');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const { data } = await axios.post(
        `${API_URL}/api/einvoice/owner-payments/bulk-upload?property_id=${form.property_id}`,
        formData,
        { headers }
      );
      toast.success(`Created ${data.created_count} payment(s)`);
      if (data.errors?.length > 0) {
        toast.warning(`${data.error_count} row(s) had errors`);
      }
      setShowUploadDialog(false);
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreatePayment = async () => {
    if (!form.property_id || !form.owner_name || !form.amount || !form.invoice_date) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!form.owner_email?.trim()) {
      toast.error('Owner Email is required for sending invoices');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/einvoice/owner-payments`, {
        ...form,
        amount: parseFloat(form.amount)
      }, { headers });
      toast.success('Payment created');
      setShowAddDialog(false);
      resetForm();
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/einvoice/owner-payments/${editingPayment.id}`, {
        ...form,
        amount: parseFloat(form.amount)
      }, { headers });
      toast.success('Payment updated');
      setShowEditDialog(false);
      setEditingPayment(null);
      resetForm();
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Delete this payment?\n\nThe record will stay visible in the list with status "Deleted" for audit purposes.')) return;

    try {
      await axios.delete(`${API_URL}/api/einvoice/owner-payments/${id}`, { headers });
      toast.success('Payment deleted (kept in list for audit)');
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete payment');
    }
  };

  // Cancel an already-submitted payment (different from Delete).
  // Use when wrong amount / customer / tax info was already submitted to LHDN.
  // Record remains visible in the list with status "Cancelled" for audit.
  const handleCancelPayment = async (id) => {
    if (!window.confirm('Cancel this payment?\n\nUse Cancel when the data was wrong (amount, customer, tax) AFTER submission. The record stays in the list with status "Cancelled".')) return;

    try {
      await axios.post(`${API_URL}/api/einvoice/owner-payments/${id}/cancel`, {}, { headers });
      toast.success('Payment cancelled (kept in list for audit)');
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel payment');
    }
  };

  const handleSubmitToLHDN = async (id) => {
    const payment = payments.find(p => p.id === id);
    const missing = validatePaymentForLHDN(payment);
    if (missing.length > 0) {
      toast.error(`Cannot submit: missing ${missing.join(', ')}`, { duration: 6000 });
      return;
    }
    try {
      const { data } = await axios.post(`${API_URL}/api/einvoice/owner-payments/${id}/submit-lhdn`, {}, { headers });
      toast.success('Submitted to LHDN');
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'LHDN submission failed');
      loadPayments();
    }
  };

  const validatePaymentForLHDN = (payment) => {
    if (!payment) return ['payment data'];
    const missing = [];
    if (!payment.owner_name?.trim()) missing.push('Owner Name');
    if (!payment.owner_tin?.trim()) missing.push('Owner TIN');
    if (!payment.amount || payment.amount <= 0) missing.push('Amount');
    if (!payment.invoice_date) missing.push('Invoice Date');
    if (!payment.property_id) missing.push('Property');
    if (!payment.description?.trim()) missing.push('Description');
    if (!payment.classification_code?.trim()) missing.push('Classification Code');
    
    // LHDN rejects dates older than 72 hours
    if (payment.invoice_date) {
      const invoiceDate = new Date(payment.invoice_date);
      const now = new Date();
      const diffDays = Math.floor((now - invoiceDate) / (1000 * 60 * 60 * 24));
      if (diffDays > 3) {
        missing.push(`Invoice Date too old (${diffDays} days ago — LHDN max ~72 hours)`);
      }
    }
    return missing;
  };

  const openErrorDetail = (payment) => {
    setErrorDetail(payment);
    setShowErrorDialog(true);
  };

  const handleBatchSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select payments to submit');
      return;
    }
    
    // Pre-validate selected payments
    const invalidPayments = [];
    selectedIds.forEach(id => {
      const p = payments.find(pay => pay.id === id);
      const missing = validatePaymentForLHDN(p);
      if (missing.length > 0) invalidPayments.push({ name: p?.owner_name || id, missing });
    });
    
    if (invalidPayments.length > 0) {
      const detail = invalidPayments.map(p => `${p.name}: ${p.missing.join(', ')}`).join('\n');
      toast.error(`${invalidPayments.length} payment(s) have missing fields:\n${detail}`, { duration: 8000 });
      return;
    }
    
    try {
      const { data } = await axios.post(`${API_URL}/api/einvoice/owner-payments/batch-submit`, selectedIds, { headers });
      if (data.success?.length > 0) {
        toast.success(`Submitted ${data.success.length} payment(s) successfully`);
      }
      if (data.failed?.length > 0) {
        const firstError = data.failed[0]?.error || 'Unknown error';
        toast.error(`${data.failed.length} submission(s) failed: ${firstError}`);
      }
      setSelectedIds([]);
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Batch submission failed');
    }
  };

  const handleBatchDelete = async () => {
    const deletableIds = selectedIds.filter(id => {
      const p = payments.find(pay => pay.id === id);
      return p && canDelete(p.status);
    });

    if (deletableIds.length === 0) {
      toast.error('No deletable payments selected (only Draft / Failed / Rejected can be deleted)');
      return;
    }

    if (!window.confirm(`Delete ${deletableIds.length} payment(s)?\n\nRecords will stay visible in the list with status "Deleted" for audit purposes.`)) return;

    let deleted = 0;
    let failed = 0;
    for (const id of deletableIds) {
      try {
        await axios.delete(`${API_URL}/api/einvoice/owner-payments/${id}`, { headers });
        deleted++;
      } catch {
        failed++;
      }
    }

    if (deleted > 0) toast.success(`Deleted ${deleted} payment(s) — records kept for audit`);
    if (failed > 0) toast.warning(`${failed} deletion(s) failed`);
    setSelectedIds([]);
    loadPayments();
  };

  const handleViewPDF = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/einvoice/owner-payments/${id}/pdf`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleDownloadPdf = async (id, invoiceNumber) => {
    toast.info('Generating PDF\u2026');
    try {
      const res = await fetch(`${API_URL}/api/einvoice/owner-payments/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        let msg = 'Failed to download PDF. Please try again.';
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Self-Billed-${(invoiceNumber || id).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.message || 'Failed to download PDF. Please try again.');
    }
  };

  const handleCheckStatus = async (id) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/einvoice/owner-payments/${id}/check-status`, {}, { headers });
      const status = data.status || data.payment?.status;
      toast.success(`Status: ${(status || 'unknown').toUpperCase()}`);
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check status');
    }
  };

  const handleSendEmail = async (payment) => {
    if (!payment.owner_email?.trim()) {
      toast.error('No email found for this owner. Please add an email address first.');
      return;
    }
    try {
      const { data } = await axios.post(`${API_URL}/api/einvoice/owner-payments/${payment.id}/send-email`, {}, { headers });
      toast.success(data.message || `Email sent to ${payment.owner_email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    }
  };

  const openInvoiceView = async (payment) => {
    setViewingPayment(payment);
    setShowInvoiceView(true);
    // Fetch company info for the invoice header
    if (!companyInfo) {
      try {
        const { data } = await axios.get(`${API_URL}/api/einvoice/portal/company`, { headers });
        setCompanyInfo(data.company || data);
      } catch { /* ignore — will show with available data */ }
    }
  };

  const getVerificationUrl = (p) => {
    if (p?.qr_code_url) return p.qr_code_url;
    const longId = p?.lhdn_long_id || p?.long_id;
    if (p?.lhdn_uuid && longId) {
      // Use sandbox URL when sandbox flag is set; default to production
      const isSandbox = p?.lhdn_environment === 'sandbox' || p?.environment === 'sandbox';
      const base = isSandbox
        ? 'https://preprod.myinvois.hasil.gov.my'
        : 'https://myinvois.hasil.gov.my';
      return `${base}/${p.lhdn_uuid}/share/${longId}`;
    }
    return null;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.property_id) params.append('property_id', filters.property_id);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      const response = await axios.get(`${API_URL}/api/einvoice/owner-payments/export?${params}`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `owner_payments_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      let msg = 'Failed to export';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {}
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  const resetForm = () => {
    setForm({
      invoice_date: new Date().toISOString().split('T')[0],
      owner_name: '',
      owner_ic_brn: '',
      owner_tin: 'EI00000000010',
      amount: '',
      description: '',
      property_id: properties[0]?.id || '',
      classification_code: '036',
      owner_address: '',
      owner_city: '',
      owner_state: '',
      owner_postal_code: '',
      owner_phone: '',
      owner_email: '',
      is_foreigner: false
    });
  };

  const openEditDialog = (payment) => {
    setEditingPayment(payment);
    const isForeigner = payment.is_foreigner || false;
    // Normalize TIN: if stored as "Foreigner" or is_foreigner flag is set, use LHDN-compliant TIN
    const rawTin = (payment.owner_tin || '').trim().toLowerCase();
    const isForeignerTin = ['foreigner', 'foreign', 'fg'].includes(rawTin);
    const effectiveIsForeigner = isForeigner || isForeignerTin;
    const normalizedTin = effectiveIsForeigner ? 'EI00000000030' : (payment.owner_tin || '');
    
    setForm({
      invoice_date: payment.invoice_date || '',
      owner_name: payment.owner_name || '',
      owner_ic_brn: payment.owner_ic_brn || '',
      owner_tin: normalizedTin,
      amount: payment.amount?.toString() || '',
      description: payment.description || '',
      property_id: payment.property_id || '',
      classification_code: payment.classification_code || '036',
      owner_address: payment.owner_address || '',
      owner_city: payment.owner_city || '',
      owner_state: payment.owner_state || '',
      owner_postal_code: payment.owner_postal_code || '',
      owner_phone: payment.owner_phone || '',
      owner_email: payment.owner_email || '',
      is_foreigner: effectiveIsForeigner
    });
    setShowEditDialog(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      validated: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      deleted: 'bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300 line-through',
      cancelled: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    };
    const icons = {
      draft: <Edit2 className="w-3 h-3 mr-1" />,
      pending: <Clock className="w-3 h-3 mr-1" />,
      submitted: <Send className="w-3 h-3 mr-1" />,
      validated: <CheckCircle className="w-3 h-3 mr-1" />,
      rejected: <XCircle className="w-3 h-3 mr-1" />,
      failed: <AlertCircle className="w-3 h-3 mr-1" />,
      deleted: <Trash2 className="w-3 h-3 mr-1" />,
      cancelled: <Ban className="w-3 h-3 mr-1" />,
    };
    return (
      <Badge className={`${styles[status] || styles.draft} border-0 flex items-center`}>
        {icons[status]}{status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  // Permissions: terminal states (deleted, cancelled) are read-only.
  const canEdit = (status) => ['draft', 'pending', 'rejected', 'failed'].includes(status);
  const canSubmit = (status) => ['draft', 'pending', 'rejected', 'failed'].includes(status);
  const canDelete = (status) => ['draft', 'failed', 'rejected'].includes(status);
  // Cancel is allowed for already-submitted records (e-Invoice spec): when wrong
  // amount / customer / tax info was already submitted to LHDN. Hides for
  // already-deleted, already-cancelled, or never-submitted draft records.
  const canCancel = (status) => ['pending', 'submitted', 'validated'].includes(status);

  const selectablePayments = payments.filter(p => canSubmit(p.status));
  const allSelected = selectablePayments.length > 0 && selectablePayments.every(p => selectedIds.includes(p.id));

  return (
    <PortalLayout>
    <div className="p-6 space-y-6" data-testid="owner-payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Owner Payments</h1>
          <p className="text-sm text-slate-500 dark:text-gray-400">
            Self-Billed Invoices (MSIC Code 036) for rental and profit sharing payments
          </p>
          <p className="text-xs font-medium text-[#4169E1] mt-1" data-testid="viewing-property-label">
            Viewing: {!filters.property_id ? 'All Properties' : (properties.find(p => p.id === filters.property_id)?.property_name || filters.property_id)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" /> Template
          </Button>
          <Button variant="outline" onClick={() => setShowUploadDialog(true)} data-testid="upload-btn">
            <Upload className="w-4 h-4 mr-2" /> Upload
          </Button>
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }} data-testid="add-payment-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Payment
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 dark:border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search invoice # or owner name..."
                value={filters.search}
                onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
                className="h-9"
                data-testid="search-input"
              />
            </div>
            
            <Select value={filters.property_id || "all"} onValueChange={v => { setFilters(f => ({ ...f, property_id: v === "all" ? "" : v })); setPage(1); setGlobalProperty(v === "all" ? null : v, properties); }}>
              <SelectTrigger className="w-[180px] h-9" data-testid="filter-property">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.status || "all"} onValueChange={v => { setFilters(f => ({ ...f, status: v === "all" ? "" : v })); setPage(1); }}>
              <SelectTrigger className="w-[140px] h-9" data-testid="filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="validated">Validated</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.source || "all"} onValueChange={v => { setFilters(f => ({ ...f, source: v === "all" ? "" : v })); setPage(1); }}>
              <SelectTrigger className="w-[130px] h-9" data-testid="filter-source">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="excel_upload">Excel Upload</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="ghost" size="sm" onClick={loadPayments} className="h-9">
              <RefreshCw className="w-4 h-4" />
            </Button>
            
            <div>
              <Label className="text-xs text-slate-500">From</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={e => { setFilters(f => ({ ...f, date_from: e.target.value })); setPage(1); }}
                className="h-9 w-[140px]"
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">To</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={e => { setFilters(f => ({ ...f, date_to: e.target.value })); setPage(1); }}
                className="h-9 w-[140px]"
                data-testid="filter-date-to"
              />
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              className="h-9"
              onClick={() => { setFilters({ property_id: '', status: '', source: '', search: '', date_from: '', date_to: '' }); setPage(1); }}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batch Actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            {selectedIds.length} selected
          </span>
          <Button size="sm" onClick={handleBatchSubmit} data-testid="batch-submit-btn">
            <Send className="w-4 h-4 mr-1" /> Submit to LHDN
          </Button>
          <Button size="sm" variant="destructive" onClick={handleBatchDelete} data-testid="batch-delete-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Quick Filter: Failed records banner */}
      {payments.some(p => p.status === 'failed') && !filters.status && (
        <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800" data-testid="failed-banner">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-400">
            {payments.filter(p => p.status === 'failed').length} payment(s) failed LHDN submission
          </span>
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 ml-auto"
            onClick={() => setFilters(f => ({ ...f, status: 'failed' }))}
            data-testid="filter-failed-btn"
          >
            <Filter className="w-3 h-3 mr-1" /> Show Failed Only
          </Button>
        </div>
      )}

      {/* Table */}
      <Card className="border-slate-200 dark:border-zinc-800">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="payments-table">
            <thead>
              <tr className="border-b border-slate-100 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50">
                <th className="p-3 text-left w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedIds(selectablePayments.map(p => p.id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </th>
                <th className="p-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">No.</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">Invoice #</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">Source</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">Owner Name</th>
                {!filters.property_id && <th className="p-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">Property</th>}
                <th className="p-3 text-right text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">Amount</th>
                <th className="p-3 text-center text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">Status</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">LHDN UUID</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">Date</th>
                <th className="p-3 text-right text-xs font-semibold text-slate-600 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={!filters.property_id ? 11 : 10} className="p-8 text-center text-slate-500">Loading...</td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={!filters.property_id ? 11 : 10} className="p-8 text-center text-slate-500">
                    No owner payments found. Create one to get started.
                  </td>
                </tr>
              ) : (
                payments.map((p, idx) => (
                  <tr key={p.id} className="border-b border-slate-50 dark:border-zinc-800/50 hover:bg-slate-50 dark:hover:bg-zinc-900/30">
                    <td className="p-3">
                      {canSubmit(p.status) && (
                        <Checkbox
                          checked={selectedIds.includes(p.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedIds([...selectedIds, p.id]);
                            } else {
                              setSelectedIds(selectedIds.filter(id => id !== p.id));
                            }
                          }}
                        />
                      )}
                    </td>
                    <td className="p-3 text-sm text-slate-600 dark:text-gray-400">
                      {(page - 1) * 20 + idx + 1}
                    </td>
                    <td className="p-3">
                      <span className="font-medium text-slate-900 dark:text-white">{p.invoice_number}</span>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="text-xs capitalize">
                        {p.source?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700 dark:text-gray-300">{p.owner_name}</span>
                      </div>
                    </td>
                    {!filters.property_id && (
                      <td className="p-3 text-sm text-slate-600 dark:text-gray-300">
                        {properties.find(pr => pr.id === p.property_id)?.property_name || '-'}
                      </td>
                    )}
                    <td className="p-3 text-right">
                      <span className="font-medium text-slate-900 dark:text-white">
                        MYR {p.amount?.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="p-3 text-center">{getStatusBadge(p.status)}</td>
                    <td className="p-3">
                      {p.lhdn_uuid ? (
                        getVerificationUrl(p) ? (
                          <a
                            href={getVerificationUrl(p)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-mono text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400"
                            title="Open validated e-Invoice on LHDN MyInvois portal"
                            data-testid={`lhdn-link-${p.invoice_number || p.id}`}
                          >
                            <span className="truncate max-w-[120px]">{p.lhdn_uuid.substring(0, 12)}…</span>
                            <ExternalLink className="w-3 h-3 shrink-0 text-emerald-500" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleCheckStatus(p.id)}
                            className="inline-flex items-center gap-1 text-xs font-mono text-slate-500 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400 cursor-pointer"
                            title="LHDN verification link not yet retrieved. Click to fetch from LHDN MyInvois."
                            data-testid={`refresh-lhdn-link-${p.invoice_number || p.id}`}
                          >
                            <span>{p.lhdn_uuid.substring(0, 12)}…</span>
                            <RefreshCw className="w-3 h-3 shrink-0 text-amber-500" />
                          </button>
                        )
                      ) : p.status === 'failed' && p.lhdn_error ? (
                        <button
                          className="text-xs text-red-600 dark:text-red-400 hover:underline cursor-pointer text-left max-w-[200px] truncate"
                          onClick={() => openErrorDetail(p)}
                          title="Click to view full error"
                          data-testid={`error-detail-btn-${p.id}`}
                        >
                          {p.lhdn_error}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-3 text-sm text-slate-600 dark:text-gray-400">{p.invoice_date}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* PDF Download button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleDownloadPdf(p.id, p.invoice_number)}
                          data-testid={`pdf-btn-${p.id}`}
                        >
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>

                        {/* Submit button for submittable statuses */}
                        {p.status === 'failed' ? (
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={() => handleSubmitToLHDN(p.id)}
                            data-testid={`resubmit-btn-${p.id}`}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Retry
                          </Button>
                        ) : canSubmit(p.status) ? (
                          <Button
                            size="sm"
                            className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => handleSubmitToLHDN(p.id)}
                            data-testid={`submit-btn-${p.id}`}
                          >
                            <Send className="w-3 h-3 mr-1" /> Submit
                          </Button>
                        ) : null}

                        {/* Check Status button for submitted/validated */}
                        {(p.status === 'submitted' || p.status === 'validated') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handleCheckStatus(p.id)}
                            data-testid={`check-btn-${p.id}`}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Check
                          </Button>
                        )}

                        {/* QR button for validated with UUID */}
                        {p.lhdn_uuid && getVerificationUrl(p) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-900/20"
                            onClick={() => { setQrPayment(p); setShowQrModal(true); }}
                            data-testid={`qr-btn-${p.id}`}
                          >
                            <QrCode className="w-3 h-3 mr-1" /> QR
                          </Button>
                        )}

                        {/* 3-dot dropdown menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-${p.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openInvoiceView(p)}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {p.lhdn_uuid && getVerificationUrl(p) && (
                              <DropdownMenuItem onClick={() => {
                                const url = getVerificationUrl(p);
                                navigator.clipboard.writeText(url);
                                toast.success('Payment verification link copied');
                              }}>
                                <Copy className="w-4 h-4 mr-2" /> Generate Payment Link
                              </DropdownMenuItem>
                            )}
                            {p.lhdn_uuid && getVerificationUrl(p) && (
                              <DropdownMenuItem onClick={() => window.open(getVerificationUrl(p), '_blank')}>
                                <ExternalLink className="w-4 h-4 mr-2" /> Verify on MyInvois
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleSendEmail(p)} data-testid={`send-email-${p.id}`}>
                              <FileText className="w-4 h-4 mr-2" /> Send Email
                            </DropdownMenuItem>
                            {canEdit(p.status) && (
                              <DropdownMenuItem onClick={() => openEditDialog(p)}>
                                <Edit2 className="w-4 h-4 mr-2" /> {p.status === 'failed' ? 'Edit & Fix' : 'Edit'}
                              </DropdownMenuItem>
                            )}
                            {p.status === 'failed' && p.lhdn_error && (
                              <DropdownMenuItem onClick={() => openErrorDetail(p)}>
                                <AlertCircle className="w-4 h-4 mr-2" /> View Error
                              </DropdownMenuItem>
                            )}
                            {canDelete(p.status) && (
                              <DropdownMenuItem onClick={() => handleDeletePayment(p.id)} className="text-red-600" data-testid={`delete-payment-${p.invoice_number || p.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            )}
                            {canCancel(p.status) && (
                              <DropdownMenuItem onClick={() => handleCancelPayment(p.id)} className="text-orange-600" data-testid={`cancel-payment-${p.invoice_number || p.id}`}>
                                <Ban className="w-4 h-4 mr-2" /> Cancel
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-zinc-800" data-testid="owner-pagination">
          <p className="text-xs text-slate-500 dark:text-gray-400">
            {total > 0 ? `Showing ${(page - 1) * 20 + 1}–${Math.min(page * 20, total)} of ${total} payments` : 'No records'}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)} data-testid="owner-page-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {pages > 0 && [...Array(Math.min(pages, 5))].map((_, i) => {
              const p = page <= 3 ? i + 1 : page - 2 + i;
              if (p < 1 || p > pages) return null;
              return (
                <Button key={p} variant={p === page ? "default" : "outline"} size="sm"
                  className={`h-7 w-7 p-0 text-xs ${p === page ? 'bg-blue-600 text-white' : ''}`}
                  onClick={() => setPage(p)} data-testid={`owner-page-${p}`}>
                  {p}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= pages}
              onClick={() => setPage(p => p + 1)} data-testid="owner-page-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Add Payment Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" /> Add Owner Payment
            </DialogTitle>
            <DialogDescription>
              Create a self-billed invoice for owner rental/profit sharing payment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Invoice Date *</Label>
                <Input
                  type="date"
                  value={form.invoice_date}
                  onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))}
                  data-testid="form-invoice-date"
                />
              </div>
              <div>
                <Label className="text-sm">Property *</Label>
                <Select value={form.property_id || undefined} onValueChange={v => setForm(f => ({ ...f, property_id: v }))}>
                  <SelectTrigger data-testid="form-property">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Owner Name / Company Name *</Label>
              <Input
                value={form.owner_name}
                onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))}
                placeholder="e.g., John Smith or ABC Sdn Bhd"
                data-testid="form-owner-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Owner IC / Company Reg No.</Label>
                <Input
                  value={form.owner_ic_brn}
                  onChange={e => setForm(f => ({ ...f, owner_ic_brn: e.target.value }))}
                  placeholder="e.g., 901234-56-7890"
                  data-testid="form-owner-ic"
                />
              </div>
              <div>
                <Label className="text-sm">Owner TIN / Company TIN</Label>
                <Input
                  value={form.owner_tin}
                  onChange={e => setForm(f => ({ ...f, owner_tin: e.target.value }))}
                  placeholder="EI00000000010"
                  data-testid="form-owner-tin"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Default: EI00000000010 | Foreigner: EI00000000030
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_foreigner"
                checked={form.is_foreigner}
                onCheckedChange={v => {
                  setForm(f => ({ 
                    ...f, 
                    is_foreigner: v,
                    owner_tin: v ? 'EI00000000030' : 'EI00000000010'
                  }));
                }}
              />
              <Label htmlFor="is_foreigner" className="text-sm">Owner is a foreigner (TIN: EI00000000030)</Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Amount (MYR) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="5000.00"
                  data-testid="form-amount"
                />
              </div>
              <div>
                <Label className="text-sm">Description *</Label>
                <Input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g., March 2025 Rental"
                  data-testid="form-description"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Classification Code *</Label>
              <Select value={form.classification_code || '036'} onValueChange={v => setForm(f => ({ ...f, classification_code: v }))}>
                <SelectTrigger data-testid="form-classification-code">
                  <SelectValue placeholder="Select classification" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="036">036 - Rental / Leasing / Property Income</SelectItem>
                  <SelectItem value="035">035 - Real Estate Activities</SelectItem>
                  <SelectItem value="062">062 - Computer Programming & Consultancy</SelectItem>
                  <SelectItem value="049">049 - Other Land Transport</SelectItem>
                  <SelectItem value="096">096 - Other Personal Service Activities</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Default: 036 (Rental/Leasing). Required by LHDN MyInvois.</p>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium mb-3">Owner Address (Optional)</p>
              <div className="space-y-3">
                <Input
                  value={form.owner_address}
                  onChange={e => setForm(f => ({ ...f, owner_address: e.target.value }))}
                  placeholder="Street address"
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={form.owner_city}
                    onChange={e => setForm(f => ({ ...f, owner_city: e.target.value }))}
                    placeholder="City"
                  />
                  <Input
                    value={form.owner_state}
                    onChange={e => setForm(f => ({ ...f, owner_state: e.target.value }))}
                    placeholder="State"
                  />
                  <Input
                    value={form.owner_postal_code}
                    onChange={e => setForm(f => ({ ...f, owner_postal_code: e.target.value }))}
                    placeholder="Postal code"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <Input
                      value={form.owner_phone}
                      onChange={e => setForm(f => ({ ...f, owner_phone: e.target.value }))}
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email *</Label>
                    <Input
                      type="email"
                      value={form.owner_email}
                      onChange={e => setForm(f => ({ ...f, owner_email: e.target.value }))}
                      placeholder="owner@email.com"
                      className={form.owner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email) ? 'border-red-300' : ''}
                      data-testid="owner-email-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePayment} disabled={submitting} data-testid="create-payment-btn">
              {submitting ? 'Creating...' : 'Create Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" /> Edit Owner Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Show LHDN error banner if editing a failed record */}
            {editingPayment?.status === 'failed' && editingPayment?.lhdn_error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg" data-testid="edit-error-banner">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-700 dark:text-red-400">LHDN Submission Failed</p>
                    <p className="text-xs text-red-600 dark:text-red-500 mt-1 break-words">{editingPayment.lhdn_error}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Invoice Date *</Label>
                <Input
                  type="date"
                  value={form.invoice_date}
                  onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))}
                  className={!form.invoice_date ? 'border-red-300' : ''}
                />
              </div>
              <div>
                <Label className="text-sm">Amount (MYR) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Owner Name / Company Name *</Label>
              <Input
                value={form.owner_name}
                onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Owner IC / Company Reg No.</Label>
                <Input
                  value={form.owner_ic_brn}
                  onChange={e => setForm(f => ({ ...f, owner_ic_brn: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm">Owner TIN / Company TIN</Label>
                <Input
                  value={form.is_foreigner ? 'EI00000000030' : form.owner_tin}
                  onChange={e => setForm(f => ({ ...f, owner_tin: e.target.value }))}
                  readOnly={form.is_foreigner}
                  className={form.is_foreigner ? 'bg-slate-50 dark:bg-zinc-900' : ''}
                  data-testid="edit-owner-tin"
                />
                {form.is_foreigner && (
                  <p className="text-xs text-blue-600 mt-1">Foreigner TIN (auto-assigned by LHDN)</p>
                )}
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Description</Label>
              <Input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>
            
            <div>
              <Label className="text-sm">Classification Code *</Label>
              <Select value={form.classification_code || '036'} onValueChange={v => setForm(f => ({ ...f, classification_code: v }))}>
                <SelectTrigger data-testid="edit-classification-code" className={!form.classification_code ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Select classification" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="036">036 - Rental / Leasing / Property Income</SelectItem>
                  <SelectItem value="035">035 - Real Estate Activities</SelectItem>
                  <SelectItem value="062">062 - Computer Programming & Consultancy</SelectItem>
                  <SelectItem value="049">049 - Other Land Transport</SelectItem>
                  <SelectItem value="096">096 - Other Personal Service Activities</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="edit_is_foreigner"
                checked={form.is_foreigner}
                onCheckedChange={v => {
                  setForm(f => ({ 
                    ...f, 
                    is_foreigner: v,
                    owner_tin: v ? 'EI00000000030' : 'EI00000000010'
                  }));
                }}
              />
              <Label htmlFor="edit_is_foreigner" className="text-sm">Owner is a foreigner</Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Owner Email *</Label>
                <Input
                  type="email"
                  value={form.owner_email}
                  onChange={e => setForm(f => ({ ...f, owner_email: e.target.value }))}
                  placeholder="owner@email.com"
                  className={form.owner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email) ? 'border-red-300' : ''}
                  data-testid="edit-owner-email"
                />
              </div>
              <div>
                <Label className="text-sm">Owner Phone</Label>
                <Input
                  value={form.owner_phone}
                  onChange={e => setForm(f => ({ ...f, owner_phone: e.target.value }))}
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium mb-3">Owner Address *</p>
              <div className="space-y-3">
                <textarea
                  value={form.owner_address}
                  onChange={e => setForm(f => ({ ...f, owner_address: e.target.value }))}
                  placeholder="Street address (e.g. 57-11-02, Mansion One, Jalan Sultan Ahmad Shah)"
                  className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="edit-owner-address"
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={form.owner_city}
                    onChange={e => setForm(f => ({ ...f, owner_city: e.target.value }))}
                    placeholder="City"
                    data-testid="edit-owner-city"
                  />
                  <Input
                    value={form.owner_state}
                    onChange={e => setForm(f => ({ ...f, owner_state: e.target.value }))}
                    placeholder="State"
                    data-testid="edit-owner-state"
                  />
                  <Input
                    value={form.owner_postal_code}
                    onChange={e => setForm(f => ({ ...f, owner_postal_code: e.target.value }))}
                    placeholder="Postal code"
                    maxLength={5}
                    data-testid="edit-owner-postal"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditingPayment(null); }}>Cancel</Button>
            <Button onClick={handleUpdatePayment} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" /> Upload Owner Payments
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file to create multiple owner payment records
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm">Select Property *</Label>
              <Select value={form.property_id || undefined} onValueChange={v => setForm(f => ({ ...f, property_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="border-2 border-dashed border-slate-200 dark:border-zinc-700 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 dark:text-gray-400 mb-3">
                Select an Excel (.xlsx) or CSV file
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                disabled={uploading || !form.property_id}
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-400"
              />
            </div>
            
            <Button variant="outline" className="w-full" onClick={handleDownloadTemplate}>
              <Download className="w-4 h-4 mr-2" /> Download Template First
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Detail Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" /> LHDN Submission Error
            </DialogTitle>
            <DialogDescription>
              Submission failed for {errorDetail?.invoice_number} - {errorDetail?.owner_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4" data-testid="error-detail-dialog">
            <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-mono text-red-700 dark:text-red-400 whitespace-pre-wrap break-words">
                {errorDetail?.lhdn_error || 'No error details available'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500">Owner:</span>
                <p className="font-medium">{errorDetail?.owner_name}</p>
              </div>
              <div>
                <span className="text-slate-500">TIN:</span>
                <p className="font-medium">{errorDetail?.owner_tin}</p>
              </div>
              <div>
                <span className="text-slate-500">Amount:</span>
                <p className="font-medium">MYR {errorDetail?.amount?.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <span className="text-slate-500">Date:</span>
                <p className="font-medium">{errorDetail?.invoice_date}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowErrorDialog(false)}>Close</Button>
            <Button
              onClick={() => {
                setShowErrorDialog(false);
                openEditDialog(errorDetail);
              }}
              data-testid="error-edit-btn"
            >
              <Edit2 className="w-4 h-4 mr-1" /> Edit & Fix
            </Button>
            <Button
              variant="default"
              onClick={() => {
                setShowErrorDialog(false);
                handleSubmitToLHDN(errorDetail.id);
              }}
              data-testid="error-resubmit-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Resubmit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice View Modal — Full LHDN-compliant view */}
      <Dialog open={showInvoiceView} onOpenChange={setShowInvoiceView}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-view-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Invoice Details <span className="font-mono text-sm text-slate-400">{viewingPayment?.invoice_number}</span>
            </DialogTitle>
            <DialogDescription>LHDN Self-Billed Invoice data</DialogDescription>
          </DialogHeader>
          {viewingPayment && (() => {
            const p = viewingPayment;
            const verifyUrl = getVerificationUrl(p);
            const isValidated = p.status === 'validated' || p.lhdn_uuid;
            const propName = properties.find(pr => pr.id === p.property_id)?.property_name || '';
            return (
              <div className="space-y-5 text-sm">
                {/* Row 1: Invoice Info + Status */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div><Label className="text-[11px] text-slate-500 uppercase tracking-wide">Invoice #</Label><p className="font-mono font-medium">{p.invoice_number}</p></div>
                  <div><Label className="text-[11px] text-slate-500 uppercase tracking-wide">Status</Label><div className="mt-0.5">{getStatusBadge(p.status)}</div></div>
                  <div><Label className="text-[11px] text-slate-500 uppercase tracking-wide">Type</Label><p>Self-Billed</p></div>
                  <div><Label className="text-[11px] text-slate-500 uppercase tracking-wide">Currency</Label><p>MYR</p></div>
                </div>

                {/* Buyer (Company) Information */}
                {companyInfo && (
                  <div className="border rounded-lg p-3 dark:border-gray-700">
                    <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide mb-2">Buyer (Self-Billing Party)</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div><Label className="text-[11px] text-slate-400">Name</Label><p>{companyInfo.company_name || companyInfo.business_name || '-'}</p></div>
                      <div><Label className="text-[11px] text-slate-400">TIN</Label><p className="font-mono">{companyInfo.tin || '-'}</p></div>
                      <div><Label className="text-[11px] text-slate-400">BRN</Label><p className="font-mono">{companyInfo.brn || '-'}</p></div>
                      <div><Label className="text-[11px] text-slate-400">SST No.</Label><p className="font-mono">{companyInfo.sst_number || '-'}</p></div>
                      <div><Label className="text-[11px] text-slate-400">MSIC Code</Label><p className="font-mono">{companyInfo.msic_code || '-'}</p></div>
                    </div>
                    {companyInfo.address && (
                      <div className="mt-2">
                        <Label className="text-[11px] text-slate-400">Address</Label>
                        <p className="text-xs">{[companyInfo.address, companyInfo.city, companyInfo.state, companyInfo.postal_code].filter(Boolean).join(', ') || '-'}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Seller (Owner) Information */}
                <div className="border rounded-lg p-3 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide mb-2">Seller (Owner/Supplier)</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div><Label className="text-[11px] text-slate-400">Name</Label><p className="font-medium">{p.owner_name || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">TIN</Label><p className="font-mono">{p.owner_tin || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">IC / BRN</Label><p className="font-mono">{p.owner_ic_brn || '-'}</p></div>
                    {p.owner_email && <div><Label className="text-[11px] text-slate-400">Email</Label><p>{p.owner_email}</p></div>}
                    {p.owner_phone && <div><Label className="text-[11px] text-slate-400">Phone</Label><p>{p.owner_phone}</p></div>}
                    {p.is_foreigner && <div><Label className="text-[11px] text-slate-400">Type</Label><Badge variant="outline" className="text-[10px]">Foreigner</Badge></div>}
                  </div>
                  {(p.owner_address || p.owner_city) && (
                    <div className="mt-2">
                      <Label className="text-[11px] text-slate-400">Address</Label>
                      <p className="text-xs">{[p.owner_address, p.owner_city, p.owner_state, p.owner_postal_code].filter(Boolean).join(', ')}</p>
                    </div>
                  )}
                </div>

                {/* Invoice Details Row */}
                <div className="border rounded-lg p-3 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide mb-2">Invoice Information</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div><Label className="text-[11px] text-slate-400">Invoice Date</Label><p>{formatDate(p.invoice_date)}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Property</Label><p>{propName || '-'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Classification Code</Label><p className="font-mono">{p.classification_code || '036'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">MSIC Code</Label><p className="font-mono">{p.msic_code || '03600'}</p></div>
                    <div><Label className="text-[11px] text-slate-400">Source</Label><Badge variant="outline" className="text-[10px] capitalize">{p.source?.replace('_', ' ') || '-'}</Badge></div>
                  </div>
                </div>

                {/* Charges Summary */}
                <div className="border rounded-lg dark:border-gray-700 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 dark:bg-zinc-900 border-b dark:border-gray-700">
                    <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide">Charges Summary</h4>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b dark:border-gray-700 bg-slate-50/50 dark:bg-zinc-900/50">
                        <th className="text-left px-3 py-1.5 font-medium text-slate-500">Description</th>
                        <th className="text-right px-3 py-1.5 font-medium text-slate-500 w-24">Amount (MYR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b dark:border-gray-800">
                        <td className="px-3 py-1.5">{p.description}</td>
                        <td className="text-right px-3 py-1.5 font-mono">{p.amount?.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</td>
                      </tr>
                      <tr className="border-b dark:border-gray-800">
                        <td className="px-3 py-1.5 text-slate-500">Tax (Exempt)</td>
                        <td className="text-right px-3 py-1.5 font-mono text-slate-500">0.00</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Financial Summary */}
                <div className="flex justify-end">
                  <div className="w-56 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">MYR {p.amount?.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Tax</span><span className="font-mono">MYR 0.00</span></div>
                    <div className="flex justify-between border-t pt-1 dark:border-gray-700"><span className="font-semibold">Total</span><span className="font-bold font-mono text-[#4169E1]">MYR {p.amount?.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span></div>
                  </div>
                </div>

                {/* LHDN Submission Data */}
                {(p.lhdn_uuid || p.lhdn_long_id || p.status === 'validated' || p.status === 'submitted') && (
                  <div className="border rounded-lg p-3 dark:border-gray-700 bg-slate-50/50 dark:bg-zinc-900/50">
                    <h4 className="text-xs font-semibold text-[#4169E1] uppercase tracking-wide mb-2">LHDN Submission Data</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-[11px] text-slate-400">UUID</Label><p className="font-mono text-xs break-all">{p.lhdn_uuid || '-'}</p></div>
                      <div><Label className="text-[11px] text-slate-400">Long ID</Label><p className="font-mono text-xs break-all">{p.lhdn_long_id || '-'}</p></div>
                      <div><Label className="text-[11px] text-slate-400">Validation Status</Label>
                        <p className={`font-medium ${p.status === 'validated' ? 'text-emerald-600' : p.status === 'rejected' ? 'text-red-600' : 'text-amber-600'}`}>
                          {(p.status || '-').toUpperCase()}
                        </p>
                      </div>
                      <div><Label className="text-[11px] text-slate-400">Submission Date</Label><p className="text-xs">{p.lhdn_submission_date ? new Date(p.lhdn_submission_date).toLocaleString('en-MY') : '-'}</p></div>
                    </div>
                    {verifyUrl && (
                      <div className="mt-3 pt-3 border-t dark:border-gray-700">
                        <div className="flex items-start gap-4">
                          <div className="bg-white p-2 rounded-lg shadow-sm border" data-testid="detail-qr-code">
                            <QRCodeSVG value={verifyUrl} size={120} level="M" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <Label className="text-[11px] text-slate-400">MyInvois Verification</Label>
                            <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Scan the QR code or click the link below to verify this e-Invoice on the LHDN MyInvois portal.</p>
                            <a href={verifyUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs break-all mt-1 inline-flex items-center gap-1" data-testid="detail-verification-link">
                              <ExternalLink className="w-3 h-3 shrink-0" /> {verifyUrl}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Failed error display */}
                {p.status === 'failed' && p.lhdn_error && (
                  <div className="border rounded-lg p-3 dark:border-gray-700 bg-red-50/50 dark:bg-red-900/10">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-semibold text-red-700 dark:text-red-400">Submission Failed</span>
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-400 break-words font-mono">{p.lhdn_error}</p>
                  </div>
                )}
              </div>
            );
          })()}
          {/* Footer Actions */}
          {viewingPayment && (
            <div className="flex justify-between items-center pt-3 border-t dark:border-gray-700">
              <Button variant="outline" size="sm" onClick={() => setShowInvoiceView(false)}>Close</Button>
              <div className="flex gap-2">
                {viewingPayment.owner_email && (
                  <Button variant="outline" size="sm" onClick={() => handleSendEmail(viewingPayment)} data-testid="view-send-email-btn">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Send Email
                  </Button>
                )}
                {getVerificationUrl(viewingPayment) && (
                  <Button variant="outline" size="sm" onClick={() => window.open(getVerificationUrl(viewingPayment), '_blank')} data-testid="open-myinvois-btn">
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open in MyInvois
                  </Button>
                )}
                <Button size="sm" onClick={() => handleDownloadPdf(viewingPayment.id, viewingPayment.invoice_number)} data-testid="download-pdf-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>

      {/* QR Code Verification Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="max-w-sm" data-testid="qr-code-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-emerald-600" /> e-Invoice Verification
            </DialogTitle>
            <DialogDescription>Scan to verify on LHDN MyInvois portal</DialogDescription>
          </DialogHeader>
          {qrPayment && (() => {
            const verifyUrl = getVerificationUrl(qrPayment);
            return (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-xl shadow-md border" data-testid="qr-modal-image">
                    <QRCodeSVG value={verifyUrl || ''} size={200} level="H" includeMargin />
                  </div>
                </div>
                <div className="text-center space-y-1">
                  <p className="text-sm font-medium text-slate-700 dark:text-gray-200">{qrPayment.invoice_number}</p>
                  <p className="text-xs text-slate-500 dark:text-gray-400">{qrPayment.owner_name}</p>
                  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" /> VALIDATED
                  </Badge>
                </div>
                <div className="text-center">
                  <p className="text-[11px] text-slate-400 dark:text-gray-500 mb-1">MyInvois Verification Link</p>
                  <a
                    href={verifyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline break-all inline-flex items-center gap-1 justify-center"
                    data-testid="qr-verification-link"
                  >
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    {verifyUrl}
                  </a>
                </div>
                <div className="flex justify-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { copyToClipboard(verifyUrl); }}
                    data-testid="qr-copy-link-btn"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy Link
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => window.open(verifyUrl, '_blank')}
                    data-testid="qr-open-portal-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open Portal
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

    </PortalLayout>
  );
}
